import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Search,
  Calendar,
  User,
  Tag,
  Eye,
  FileText
} from 'lucide-react';
import WebsiteLayout from '@/components/layout/WebsiteLayout';

export default function BlogsPage() {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, searchTerm, categoryFilter]);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?status=published`);
      setBlogs(response.data);
      const uniqueCategories = [...new Set(response.data.map(blog => blog.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = () => {
    let filtered = [...blogs];
    if (searchTerm) {
      filtered = filtered.filter(
        (blog) =>
          blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((blog) => blog.category === categoryFilter);
    }
    setFilteredBlogs(filtered);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <WebsiteLayout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-blue-400 font-semibold mb-4 tracking-wide uppercase text-sm">Our Blog</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Insights & Resources
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl">
            Stay updated with the latest trends, tips, and insights from our team of experts.
          </p>
        </div>
      </section>

      {/* Blogs */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-48 h-12 bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all" className="text-white hover:bg-slate-700">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat} className="text-white hover:bg-slate-700">{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-slate-400 mb-6">Showing {filteredBlogs.length} article{filteredBlogs.length !== 1 ? 's' : ''}</p>

          {loading ? (
            <div className="flex justify-center py-12"><div className="spinner" /></div>
          ) : filteredBlogs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <Card 
                  key={blog.id} 
                  className="border border-slate-700 hover:border-slate-600 hover:shadow-xl hover:shadow-blue-500/10 transition-all cursor-pointer overflow-hidden bg-slate-900"
                  onClick={() => { setSelectedBlog(blog); setViewDialogOpen(true); }}
                >
                  <div className="h-48 bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                    <FileText className="w-12 h-12 text-white/50" />
                  </div>
                  <CardContent className="p-6">
                    <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30 mb-3">
                      {blog.category}
                    </Badge>
                    <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{blog.title}</h3>
                    <p className="text-slate-400 text-sm line-clamp-3 mb-4">{blog.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{blog.author}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(blog.published_at || blog.created_at)}</span>
                      </div>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{blog.views || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No articles found</h3>
              <p className="text-slate-400">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </section>

      {/* Blog Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{selectedBlog?.title}</DialogTitle>
          </DialogHeader>
          {selectedBlog && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{selectedBlog.category}</Badge>
                <span className="flex items-center gap-1"><User className="w-4 h-4" />{selectedBlog.author}</span>
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(selectedBlog.published_at || selectedBlog.created_at)}</span>
              </div>
              <div className="border-l-4 border-blue-500 pl-4 italic text-slate-600">{selectedBlog.excerpt}</div>
              <div className="prose prose-slate max-w-none"><p className="whitespace-pre-wrap">{selectedBlog.content}</p></div>
              {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-6 border-t">
                  <Tag className="w-4 h-4 text-slate-400" />
                  {selectedBlog.tags.map((tag) => (<Badge key={tag} variant="secondary">#{tag}</Badge>))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </WebsiteLayout>
  );
}
